import { Box, Button, Paper, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { LEVEL_LABEL } from '@app/shared';
import type { UserDto } from '@app/shared';
import { DataTable, rowNumber } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { StatusChip } from '../../components/StatusChip';
import { formatDateTime } from '../../lib/format';
import { useUsers } from './api';

/** Search user (PC871): find a user of any level by name or login ID. */
export function SearchUserPage() {
  const [text, setText] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const users = useUsers({ q: query, page, pageSize, sort: 'name' }, query.length > 0);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setQuery(text.trim());
    setPage(1);
  };

  const columns: Column<UserDto>[] = [
    { key: '#', header: '#', render: (_u, i) => rowNumber(i, page, pageSize), width: 50 },
    { key: 'name', header: 'User Details', render: (u) => u.name },
    { key: 'loginId', header: 'Login ID', render: (u) => u.loginId },
    { key: 'group', header: 'User Group', render: (u) => u.group?.name ?? '' },
    { key: 'level', header: 'User Type', render: (u) => LEVEL_LABEL[u.level] },
    { key: 'createdBy', header: 'Created By', render: (u) => u.createdBy?.loginId ?? '' },
    {
      key: 'createdAt',
      header: 'Created Datetime',
      nowrap: true,
      render: (u) => formatDateTime(u.createdAt),
    },
    {
      key: 'status',
      header: 'Status',
      render: (u) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <StatusChip status={u.status} />
          {u.locked ? <StatusChip status="Locked" /> : null}
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Typography variant="h3" sx={{ mb: 1 }}>
        Search User
      </Typography>
      <Paper
        variant="outlined"
        component="form"
        onSubmit={submit}
        sx={{ p: 1.5, mb: 1.5, display: 'flex', gap: 1, maxWidth: 560 }}
      >
        <TextField
          label="Search by name or login id"
          value={text}
          onChange={(e) => setText(e.target.value)}
          autoFocus
        />
        <Button type="submit" variant="contained" disabled={!text.trim()}>
          Search
        </Button>
      </Paper>
      {query ? (
        <DataTable
          label="User search results"
          columns={columns}
          rows={users.data?.data}
          rowKey={(u) => u.id}
          loading={users.isFetching}
          error={users.error}
          onRetry={() => void users.refetch()}
          page={page}
          pageSize={pageSize}
          total={users.data?.total ?? 0}
          onPageChange={setPage}
          onPageSizeChange={(s) => {
            setPageSize(s);
            setPage(1);
          }}
          pageSizeOptions={[10, 20, 50, 100]}
        />
      ) : null}
    </Box>
  );
}
